"use client";

import * as React from "react";

import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

/**
 * HoverTip — the one primitive for SUPPLEMENTARY information on hover/focus.
 *
 * Why a wrapper over `Tooltip`: informational hovers were being hand-rolled per
 * surface (raw `title=` attributes, bespoke absolutely-positioned divs, ad-hoc
 * Radix wiring with inconsistent delays and widths), so the same affordance
 * looked and behaved differently on every page. This centralises the three
 * things that were drifting — open delay, content width/typography, and the
 * a11y contract — so a hint reads the same in Hub, the website, and the email
 * client.
 *
 * ## The rule this component encodes
 *
 * A HoverTip is **never the only way** to reach the information it shows.
 * Hover does not exist on touch devices and Radix (correctly) does not open
 * tooltips on tap, so anything hover-only is invisible to a phone user. Callers
 * must keep the fact reachable another way — a visible label, the row's own
 * detail view, or (cheapest) an `aria-label`/`title` on the trigger that states
 * the same thing. The `label` prop exists for exactly that: pass it and the
 * trigger's accessible name carries the hint text too, so screen-reader and
 * touch users are not second-class.
 *
 * Keyboard users get it for free: Radix opens on focus as well as hover, so a
 * focusable trigger (button/link) shows the tip when tabbed to.
 *
 * ## Usage
 *
 * ```tsx
 * <HoverTip tip="5 times available · 9:00 AM – 4:30 PM CDT">
 *   <button type="button">15</button>
 * </HoverTip>
 * ```
 *
 * The child must accept a ref and spread props (it is rendered via Radix
 * `asChild`) — a plain DOM element or a `forwardRef` component. See the
 * main-RADIX-ASCHILD rule.
 */
export interface HoverTipProps {
  /**
   * The hint. Rendered inside the tooltip; when `label` is not given and this
   * is a string it is ALSO used as the trigger's accessible name.
   */
  tip: React.ReactNode;
  /** The trigger. Must forward refs + spread props (Radix `asChild`). */
  children: React.ReactElement;
  /**
   * Accessible name for the trigger, so the hint is not hover-only. Defaults to
   * `tip` when `tip` is a string. Pass `null` to opt out — only correct when the
   * trigger already has its own accessible name that covers the same fact.
   */
  label?: string | null;
  /** Preferred side. Radix flips it when the viewport is tight. */
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  /**
   * Open delay in ms. Default 150 — fast enough to feel like part of the
   * element, slow enough that sweeping the pointer across a grid of triggers
   * does not strobe.
   */
  delayDuration?: number;
  /** Extra classes for the tooltip content. */
  className?: string;
  /**
   * Skip rendering the tooltip entirely (renders `children` untouched). Handy
   * when the hint is conditional — avoids a `{cond ? <HoverTip>… : …}` fork
   * that duplicates the trigger markup.
   */
  disabled?: boolean;
}

export function HoverTip({
  tip,
  children,
  label,
  side = "top",
  align = "center",
  delayDuration = 150,
  className,
  disabled = false,
}: HoverTipProps) {
  if (disabled || tip === null || tip === undefined || tip === "") {
    return children;
  }

  // Keep the hint reachable without a pointer. `label === null` is an explicit
  // opt-out; `undefined` falls back to the tip text when it is a plain string.
  const accessibleName =
    label === null ? undefined : label ?? (typeof tip === "string" ? tip : undefined);

  const trigger = accessibleName
    ? React.cloneElement(children, {
        "aria-label": accessibleName,
      } as React.HTMLAttributes<HTMLElement>)
    : children;

  return (
    <Tooltip delayDuration={delayDuration}>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        collisionPadding={8}
        className={cn("max-w-[16rem] text-xs leading-snug", className)}
      >
        {tip}
      </TooltipContent>
    </Tooltip>
  );
}

HoverTip.displayName = "HoverTip";
